/**
 * AdvisorGuidedStart — Release 0.9 Fase 1
 *
 * Experiencia full-screen: el usuario descubre sus preferencias hablando
 * con Habita Advisor. No es un formulario ni un chatbot.
 *
 * Cada respuesta escribe al Confidence Graph existente vía
 * `advisor.setConfidenceAttribute()` y actualiza el estado local.
 *
 * Actos incluidos en esta fase:
 *   1. Bienvenida
 *   2. Fundamentos (operation → city → neighborhood → property_type)
 *   3. Recomendaciones tempranas (después del 3er turno respondido)
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Compass, ArrowRight, ArrowLeft, Check, Sparkles, Home as HomeIcon,
  Building2, Briefcase, Coffee, DollarSign, BedDouble, Shield, TrendingUp, Clock,
  Footprints, Bus, Bike, Car,
} from 'lucide-react';
import advisor from '../lib/advisor';
import SEO from '../components/SEO';
import HabitaScoreBadge, { HabitaScorePill, HabitaScoreHeadline } from '../components/HabitaScoreBadge';
import HabitaScoreBreakdown from '../components/HabitaScoreBreakdown';
import DeepReasonsCard from '../components/DeepReasonsCard';
import FlashFinancialSim from '../components/FlashFinancialSim';
import KeyLocationInput from '../components/advisor/KeyLocationInput';
import PriorityRanker from '../components/advisor/PriorityRanker';
import CalibrationQuestion from '../components/advisor/CalibrationQuestion';
import { buildPropertyWhatsAppLink, WhatsAppIcon } from '../lib/whatsapp';
import { api } from '../lib/api';
import AdvisorCaptureForm, { buildAdvisorContext, intentFromOperation } from '../components/advisor/AdvisorCaptureForm';
import { getSessionId } from '../lib/analytics';

/* Estado de captura del comprador — por sesión (regla del silencio):
 * · si ya capturamos un lead comprador, no volvemos a mostrar el form general
 * · si el usuario lo rechazó, no re-mostrar (salvo intención fuerte: firm pick) */
const BUYER_LEAD_KEY = 'habita_buyer_lead';
const BUYER_DISMISS_KEY = 'habita_buyer_capture_dismissed';
function getBuyerLead() {
  try { return JSON.parse(sessionStorage.getItem(BUYER_LEAD_KEY) || 'null'); } catch { return null; }
}
function setBuyerLead(info) {
  try { sessionStorage.setItem(BUYER_LEAD_KEY, JSON.stringify(info)); } catch { /* noop */ }
}
function isBuyerCaptureDismissed() {
  try { return sessionStorage.getItem(BUYER_DISMISS_KEY) === '1'; } catch { return false; }
}
function dismissBuyerCapture() {
  try { sessionStorage.setItem(BUYER_DISMISS_KEY, '1'); } catch { /* noop */ }
}


/* ────────────────────────────────────────────── Turnos deterministas
 * Cada turno describe: qué atributo escribe, qué opciones ofrece y
 * cuándo aplica. La secuencia se calcula on-the-fly según el Graph actual.
 */
const TURNS = [
  {
    id: 't_op',
    key: 'operation',
    line: 'Perfecto. Empecemos por lo más simple.',
    prompt: '¿Estás buscando comprar o arrendar?',
    hint: 'Puedes cambiarlo después si necesitas.',
    options: [
      { value: 'venta', label: 'Comprar', icon: HomeIcon },
      { value: 'arriendo', label: 'Arrendar', icon: Building2 },
    ],
    layout: 'big-cards',
  },
  {
    id: 't_objective',
    key: 'objective',
    line: 'Ok. Ahora una pieza clave.',
    prompt: '¿Cuál es tu objetivo con esta decisión?',
    hint: 'Cambia bastante cómo priorizo las opciones — sé sincero conmigo.',
    options: [
      { value: 'vivir', label: 'Vivir en ella', icon: HomeIcon },
      { value: 'invertir', label: 'Invertir (renta o valorización)', icon: TrendingUp },
    ],
    layout: 'big-cards',
    // Sólo aparece cuando operation=venta (invertir arrendando no tiene sentido)
    condition: (answers) => answers.operation === 'venta',
  },
  {
    id: 't_city',
    key: 'city',
    line: 'Gracias. Ya vamos entendiendo qué buscas.',
    prompt: '¿En qué ciudad?',
    hint: 'Empieza con la principal — luego podemos sumar zonas específicas.',
    options: [
      { value: 'Bogotá', label: 'Bogotá' },
      { value: 'Medellín', label: 'Medellín' },
      { value: 'Cali', label: 'Cali' },
      { value: 'Barranquilla', label: 'Barranquilla' },
      { value: 'Cartagena', label: 'Cartagena' },
    ],
    allowsFreeText: true,
    freeTextPlaceholder: 'Otra ciudad…',
    layout: 'chips',
  },
  {
    id: 't_type',
    key: 'property_type',
    line: 'Bien. Ya tengo una idea más clara.',
    prompt: '¿Qué tipo de inmueble te imaginas?',
    hint: 'Con esto podemos priorizar las opciones que se ajusten.',
    options: [
      { value: 'apartamento', label: 'Apartamento', icon: Building2 },
      { value: 'casa', label: 'Casa', icon: HomeIcon },
      { value: 'oficina', label: 'Oficina', icon: Briefcase },
      { value: 'local', label: 'Local', icon: Coffee },
    ],
    layout: 'icon-grid',
  },
  {
    id: 't_neighborhood',
    key: 'neighborhood',
    line: 'Excelente. Ya tenemos los cimientos.',
    prompt: '¿Ya tienes claro qué zona te gustaría?',
    hint: 'Si aún no lo tienes claro, no hay problema — podemos descubrirlo juntos.',
    options: [], // dinámico por ciudad
    dynamicOptionsByCity: {
      'Bogotá': ['Chapinero', 'Usaquén', 'Suba', 'Chicó', 'Centro'],
      'Medellín': ['El Poblado', 'Laureles', 'Envigado', 'Belén'],
      'Cali': ['Ciudad Jardín', 'Granada', 'San Fernando'],
    },
    allowsSkip: true,
    skipLabel: 'Aún no lo sé — ayúdame a descubrirlo',
    skipValue: '__unknown__',
    allowsFreeText: true,
    freeTextPlaceholder: 'Otra zona…',
    layout: 'chips',
  },
  {
    id: 't_budget',
    key: 'budget',
    line: 'Vamos afinando. Con esto podré filtrar mejor.',
    prompt: '¿Hasta cuánto podrías invertir?',
    hint: 'Es un rango orientativo — siempre podemos ajustar más adelante.',
    options: [
      { value: 500_000_000, label: 'Hasta $500M' },
      { value: 1_000_000_000, label: 'Hasta $1.000M' },
      { value: 2_000_000_000, label: 'Hasta $2.000M' },
      { value: 5_000_000_000, label: 'Hasta $5.000M' },
      { value: 999_999_999_999, label: 'Sin tope claro' },
    ],
    dynamicOptionsByOperation: {
      arriendo: [
        { value: 2_000_000, label: 'Hasta $2M/mes' },
        { value: 4_000_000, label: 'Hasta $4M/mes' },
        { value: 7_000_000, label: 'Hasta $7M/mes' },
        { value: 12_000_000, label: 'Hasta $12M/mes' },
        { value: 999_999_999_999, label: 'Sin tope claro' },
      ],
    },
    allowsFreeText: true,
    freeTextPlaceholder: 'Otro monto (ej. 1500000000)…',
    freeTextParser: 'number',
    layout: 'chips',
    icon: DollarSign,
  },
  {
    id: 't_beds',
    key: 'beds',
    line: 'Perfecto. Cada respuesta hace las opciones más certeras.',
    prompt: '¿Cuántas habitaciones necesitas como mínimo?',
    hint: 'Piensa en el mínimo — luego priorizamos las que ofrecen más.',
    options: [
      { value: 1, label: '1' },
      { value: 2, label: '2' },
      { value: 3, label: '3' },
      { value: 4, label: '4 o más' },
    ],
    layout: 'icon-grid',
    icon: BedDouble,
  },
  {
    id: 't_move_horizon',
    key: 'move_horizon',
    line: 'Bien. Un dato más de contexto.',
    prompt: '¿Cuándo planeas mudarte?',
    hint: 'Con esto sabemos si tiene sentido mostrarte proyectos que aún se están construyendo.',
    options: [
      { value: 'en_3_meses', label: 'En 3 meses', desc: 'Necesito algo listo pronto' },
      { value: '3_a_6_meses', label: '3 a 6 meses', desc: 'Buscando activamente' },
      { value: '6_a_12_meses', label: '6 a 12 meses', desc: 'Explorando opciones' },
      { value: 'mas_de_1_ano', label: 'Más de 1 año', desc: 'Sin prisa' },
    ],
    layout: 'big-cards',
    icon: Clock,
    // Solo aplica cuando el objetivo es vivir (no aplica a inversores).
    condition: (answers) => (answers.objective || 'vivir') === 'vivir',
  },
  {
    id: 't_new_construction_pref',
    key: 'new_construction_pref',
    line: 'Con ese horizonte, tienes una ventaja: puedes considerar vivienda nueva.',
    prompt: '¿Te interesan proyectos sobre planos o vivienda para estrenar?',
    hint: 'Sobre planos suele tener mejor precio de entrada; estrenar te permite mudarte antes.',
    options: [
      { value: 'sobre_planos', label: 'Sobre planos', desc: 'Compro ahora, entrego en 1-2 años' },
      { value: 'estrenar', label: 'Para estrenar', desc: 'Entrego pronto, sin remodelar' },
      { value: 'ambos', label: 'Ambas me interesan', desc: 'Muéstrame las dos opciones' },
      { value: 'me_da_igual', label: 'No es prioridad', desc: 'Prefiero vivienda usada' },
    ],
    layout: 'big-cards',
    icon: Building2,
    // Sólo aparece cuando el usuario tiene horizonte largo (>1 año) y no es inversor.
    condition: (answers) => answers.move_horizon === 'mas_de_1_ano' && (answers.objective || 'vivir') === 'vivir',
  },
  {
    id: 't_mobility',
    key: 'mobility_mode',
    line: 'Ahora una pieza clave para ubicar bien.',
    prompt: '¿Cómo te mueves principalmente?',
    hint: 'Con esto calculamos el Habita Score de cada opción según tu forma de moverte.',
    options: [
      { value: 'a_pie', label: 'A pie', icon: Footprints },
      { value: 'transporte_publico', label: 'Transporte público', icon: Bus },
      { value: 'bicicleta', label: 'Bicicleta', icon: Bike },
      { value: 'carro', label: 'Carro', icon: Car },
    ],
    layout: 'big-cards',
  },
  {
    id: 't_key_location',
    key: 'key_location',
    line: 'Perfecto. Ahora el destino que más importa.',
    prompt: '¿Dónde pasas la mayor parte de tu tiempo?',
    hint: 'Dirección del trabajo, universidad o una referencia (ej: Calle 100 con 15, Universidad Nacional).',
    layout: 'text-input',
    placeholder: 'Ej: Calle 100 con 15, Bogotá',
    allowsEmpty: true,
    icon: Briefcase,
  },
  {
    id: 't_work_mode',
    key: 'work_mode',
    line: 'Y una última pieza sobre tu día a día.',
    prompt: '¿Cuál es tu esquema de trabajo?',
    hint: 'Esto me ayuda a saber cuánto peso darle a la distancia.',
    options: [
      { value: 'remoto', label: '100% Remoto', desc: 'Trabajo desde casa' },
      { value: 'hibrido', label: 'Híbrido', desc: 'Mezcla oficina + casa' },
      { value: 'presencial', label: '100% Presencial', desc: 'Voy a la oficina todos los días' },
    ],
    layout: 'big-cards',
    // Sólo aplica si el usuario dejó una ubicación clave (útil para el score)
    condition: (answers) => Boolean(answers.key_location),
  },
  {
    id: 't_priorities',
    key: 'priorities',
    line: 'Antes de mostrarte todo, quiero saber qué te importa más.',
    prompt: 'Ordena estas 4 cosas de mayor a menor prioridad.',
    hint: 'La #1 pesará más en tu Habita Score. Puedes moverlas con las flechas ↑ ↓.',
    layout: 'priority-ranker',
    allowsEmpty: false,
  },
  {
    id: 't_must_haves',
    key: 'must_haves',
    line: 'Última pieza para armar tu selección.',
    prompt: '¿Qué es indispensable para ti?',
    hint: 'Marca todo lo que sea imprescindible. Puedes dejar vacío si no aplica.',
    options: [
      { value: 'parqueadero', label: 'Parqueadero' },
      { value: 'ascensor', label: 'Ascensor' },
      { value: 'vigilancia', label: 'Vigilancia' },
      { value: 'balcón', label: 'Balcón' },
    ],
    layout: 'multi-toggle',
    allowsEmpty: true,
    icon: Shield,
  },
  {
    id: 't_expected_roi',
    key: 'expected_roi',
    line: 'Bien. Ahora hablemos de retorno.',
    prompt: '¿Qué rentabilidad anual esperas?',
    hint: 'Es la renta anual como % del precio de compra. En Colombia lo razonable oscila entre 4% y 8%.',
    options: [
      { value: 4, label: '≥ 4% anual', desc: 'Renta estable, poca exigencia' },
      { value: 6, label: '≥ 6% anual', desc: 'Objetivo balanceado' },
      { value: 8, label: '≥ 8% anual', desc: 'Retorno agresivo' },
      { value: 10, label: '≥ 10% anual', desc: 'Muy alto — poco común' },
    ],
    layout: 'chips',
    icon: TrendingUp,
    // Sólo aplica cuando el objetivo es invertir
    condition: (answers) => answers.objective === 'invertir',
  },
  {
    id: 't_investment_horizon',
    key: 'investment_horizon',
    line: 'Y para terminar.',
    prompt: '¿Cuál es tu horizonte de inversión?',
    hint: 'El tiempo que planeas mantener la propiedad. Más horizonte = más peso a la valorización.',
    options: [
      { value: 2, label: '2 años' },
      { value: 5, label: '5 años' },
      { value: 10, label: '10 años' },
      { value: 15, label: '15+ años' },
    ],
    layout: 'chips',
    icon: Clock,
    condition: (answers) => answers.objective === 'invertir',
  },
];


const UNDERSTOOD_LABELS = {
  operation: 'operación',
  objective: 'objetivo',
  city: 'ciudad',
  neighborhood: 'zona',
  property_type: 'tipo de inmueble',
  budget: 'presupuesto',
  beds: 'habitaciones',
  must_haves: 'indispensables',
  mobility_mode: 'movilidad',
  key_location: 'ubicación clave',
  work_mode: 'esquema de trabajo',
  priorities: 'prioridades',
  expected_roi: 'rentabilidad esperada',
  investment_horizon: 'horizonte',
  move_horizon: 'plazo de mudanza',
  new_construction_pref: 'preferencia vivienda nueva',
  parking: 'parqueadero',
  commute: 'desplazamiento',
};


/* Formato específico por atributo para la sidebar "Lo que ya entendí" */
function formatUnderstoodValue(key, value) {
  if (value === null || value === undefined) return '—';
  if (key === 'budget') {
    if (typeof value === 'number') {
      if (value >= 999_999_999_000) return 'sin tope';
      // Nomenclatura colombiana: siempre en millones ('M'), nunca 'B'.
      if (value >= 1_000_000) return `hasta $${Math.round(value / 1_000_000).toLocaleString('es-CO')}M`;
      return `hasta $${value.toLocaleString('es-CO')}`;
    }
  }
  if (key === 'beds') return `${value}${value >= 4 ? '+' : ''}`;
  if (key === 'must_haves') {
    if (Array.isArray(value)) {
      if (value.length === 0) return 'ninguno prioritario';
      return value.join(', ');
    }
  }
  if (key === 'objective') return value === 'invertir' ? 'invertir' : 'vivir en ella';
  if (key === 'mobility_mode') {
    return { a_pie: 'a pie', transporte_publico: 'transporte público', bicicleta: 'bicicleta', carro: 'carro' }[value] || value;
  }
  if (key === 'key_location') {
    if (value && typeof value === 'object') return value.short_label || value.label || 'ubicación clave';
    return String(value || 'anotada');
  }
  if (key === 'work_mode') {
    return { remoto: '100% remoto', hibrido: 'híbrido', presencial: '100% presencial' }[value] || value;
  }
  if (key === 'priorities') {
    if (Array.isArray(value) && value.length > 0) {
      const map = { precio: 'Precio', ubicacion: 'Ubicación', espacio: 'Espacio', tiempo: 'Tiempo' };
      return value.map((v) => map[v] || v).join(' > ');
    }
    return 'sin orden';
  }
  if (key === 'expected_roi') return `≥ ${value}% anual`;
  if (key === 'investment_horizon') return `${value} año${value === 1 ? '' : 's'}`;
  if (key === 'move_horizon') {
    return { en_3_meses: 'en 3 meses', '3_a_6_meses': '3 a 6 meses', '6_a_12_meses': '6 a 12 meses', mas_de_1_ano: 'más de 1 año' }[value] || value;
  }
  if (key === 'new_construction_pref') {
    return { sobre_planos: 'sobre planos', estrenar: 'para estrenar', ambos: 'ambas', me_da_igual: 'no es prioridad' }[value] || value;
  }
  return typeof value === 'string' ? value : JSON.stringify(value);
}


/* Micro-reacciones humanas del Advisor tras cada respuesta.
 * Se muestran en la CIMA del próximo turno como una "burbuja" italic,
 * para que cada respuesta se sienta escuchada — no solo almacenada. */
const MICRO_REACTIONS = {
  operation: {
    venta: 'Comprar. Anotado — eso cambia bastante lo que priorizamos.',
    arriendo: 'Arrendar. Anotado — flexibilidad y velocidad ganan peso.',
    _default: 'Anotado. Ya lo tengo presente.',
  },
  city: {
    'Bogotá': 'Bogotá. Buena elección — tenemos buena cobertura ahí.',
    'Medellín': 'Medellín. Vale — mucha oferta interesante.',
    'Cali': 'Cali. Perfecto — vamos a mirar bien las zonas.',
    'Barranquilla': 'Barranquilla. Anotado.',
    'Cartagena': 'Cartagena. Anotado — mercado con dinámica propia.',
    _default: 'Perfecto. Anoté la ciudad.',
  },
  property_type: {
    apartamento: 'Apartamento. Vale — vamos a filtrar por eso.',
    casa: 'Casa. Ok — normalmente más metros y menos administración.',
    oficina: 'Oficina. Anotado — cambia los criterios que priorizo.',
    local: 'Local. Anotado — miramos flujo y ubicación con lupa.',
    _default: 'Perfecto. Con eso puedo priorizar.',
  },
  neighborhood: {
    _default: 'Anotado. La zona nos ayuda a priorizar mucho.',
    __unknown__: 'Sin problema — podemos descubrirlo juntos a medida que veas propiedades.',
  },
  budget: {
    _default: 'Presupuesto anotado — con eso puedo filtrar bien.',
  },
  beds: {
    1: '1 habitación. Vale — hay muchas opciones en ese rango.',
    2: '2 habitaciones. Anotado.',
    3: '3 habitaciones. Anotado.',
    4: '4 o más. Anotado — filtramos por tamaño.',
    _default: 'Anotado — filtramos por habitaciones mínimas.',
  },
  must_haves: {
    _default: 'Anotado. Voy a priorizar propiedades que cumplan con esto.',
  },
  mobility_mode: {
    a_pie: 'A pie. Voy a favorecer barrios walkable.',
    transporte_publico: 'Transporte público. Priorizaré cercanía a rutas y estaciones.',
    bicicleta: 'Bici. Voy a preferir zonas con ciclorrutas y buen relieve.',
    carro: 'Carro. Priorizaré acceso vial y parqueadero.',
    _default: 'Perfecto — con esto pondero la movilidad de cada opción.',
  },
  key_location: {
    _default: 'Anotado. Voy a calcular cuánto tarda cada opción desde ese punto.',
  },
  work_mode: {
    remoto: '100% remoto. Voy a subir el peso del comfort en casa y bajar el de distancia.',
    hibrido: 'Híbrido. Balanceo distancia y comfort.',
    presencial: '100% presencial. La distancia a tu ubicación clave se vuelve prioridad.',
    _default: 'Perfecto — ajusto los pesos del Habita Score con esto.',
  },
  priorities: {
    _default: 'Anotado — la #1 pesará más en el Habita Score.',
  },
  objective: {
    vivir: 'Vivir en ella. Anotado — priorizaré confort, zona y calidad de vida.',
    invertir: 'Invertir. Perfecto — voy a mirar rentabilidad y valorización con lupa.',
    _default: 'Perfecto. Ese objetivo me guía.',
  },
  expected_roi: {
    _default: 'Anotado. Filtraré propiedades que puedan alcanzar ese retorno.',
  },
  investment_horizon: {
    _default: 'Perfecto. Con ese horizonte pondero renta y valorización.',
  },
  move_horizon: {
    en_3_meses: 'En 3 meses. Con esa urgencia priorizo inmuebles listos para mudarte.',
    '3_a_6_meses': '3 a 6 meses. Anotado — hay margen para elegir con calma.',
    '6_a_12_meses': '6 a 12 meses. Perfecto — podemos mirar opciones más ambiciosas.',
    mas_de_1_ano: 'Más de 1 año. Interesante — puedes considerar proyectos sobre planos con precio de entrada.',
    _default: 'Anotado. Ese plazo cambia qué opciones te muestro.',
  },
  new_construction_pref: {
    sobre_planos: 'Sobre planos. Voy a priorizar proyectos en preventa con mejor precio de entrada.',
    estrenar: 'Para estrenar. Filtro proyectos cerca de entrega — sin remodelar, todo nuevo.',
    ambos: 'Ambas. Te muestro proyectos en cualquier etapa junto con vivienda usada.',
    me_da_igual: 'Perfecto — priorizo vivienda usada, ya construida.',
  },
};

function reactionFor(key, value) {
  const bag = MICRO_REACTIONS[key];
  if (!bag) return null;
  return bag[value] || bag._default || null;
}


/* ────────────────────────────────────────────── Component */

// Mapa de deep-linking: `?turn=<key>` desde /mi-decisión → salta directo a
// ese turno para editar/refinar. Aceptamos las claves que expone el Workspace
// además de las claves internas de los turnos.
const DEEP_LINK_TURN_KEY_MAP = {
  operation: 't_op',
  city: 't_city',
  type: 't_type',            // workspace usa `type`
  property_type: 't_type',   // guided usa `property_type`
  neighborhood: 't_neighborhood',
  zone: 't_neighborhood',
};

function _turnIndexById(id) {
  return TURNS.findIndex((t) => t.id === id);
}

export default function AdvisorGuidedStart() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [step, setStep] = useState(-1); // -1 = welcome screen
  const [answers, setAnswers] = useState({});
  const [freeText, setFreeText] = useState('');
  const [saving, setSaving] = useState(false);
  const [recommendations, setRecommendations] = useState(null);
  const [ctxReady, setCtxReady] = useState(false);
  const [reaction, setReaction] = useState(null); // micro-reacción del turno anterior
  const [multiSelection, setMultiSelection] = useState([]); // para turnos multi-toggle
  const cardRef = useRef(null);

  // Timers de telemetría — persistentes durante el ciclo de vida del componente.
  const sessionStartMs = useRef(Date.now());
  const turnStartMs = useRef(Date.now());
  const currentTurnKeyRef = useRef(null);
  const recsShownRef = useRef(false);
  const finishedFiredRef = useRef(false);
  const startedFiredRef = useRef(false);

  useEffect(() => {
    (async () => {
      await advisor.getOrStartContext({ entryPoint: 'other', intent: 'guided_start' });
      setCtxReady(true);
      // Idempotencia: en dev React StrictMode invoca este effect 2x. La
      // telemetría de 'started' debe reportarse una sola vez por montaje real.
      if (startedFiredRef.current) return;
      startedFiredRef.current = true;
      const deepTurn = searchParams.get('turn');
      advisor.recordGuidedEvent('started', {
        extra: { deep_link_turn: deepTurn || 'none' },
      });
      // Deep link: si viene `?turn=<key>`, saltamos directo a ese turno.
      if (deepTurn) {
        const targetTurnId = DEEP_LINK_TURN_KEY_MAP[deepTurn];
        const idx = targetTurnId ? _turnIndexById(targetTurnId) : -1;
        if (idx >= 0) {
          advisor.recordGuidedEvent('resumed', {
            turnKey: TURNS[idx].key, turnIndex: idx,
            extra: { via: 'workspace_edit' },
          });
          setStep(idx);
          turnStartMs.current = Date.now();
          currentTurnKeyRef.current = TURNS[idx].key;
          // Limpiar el query param para que no re-dispare al recargar
          searchParams.delete('turn');
          setSearchParams(searchParams, { replace: true });
        }
      }
    })();
  }, []);

  const currentTurn = step >= 0 && step < TURNS.length ? TURNS[step] : null;
  const finished = step >= TURNS.length;

  // Skip any conditional turn that no longer applies (e.g. investor-only turns
  // when objective=vivir). Runs after each `answers` update.
  useEffect(() => {
    if (step < 0 || step >= TURNS.length) return;
    const t = TURNS[step];
    if (t.condition && !t.condition(answers)) {
      // Advance without recording an answer.
      setStep((s) => s + 1);
    }
  }, [step, answers]);

  // Only count turns that actually apply, so progress % is accurate.
  const applicableTurnsCount = TURNS.filter(
    (t) => !t.condition || t.condition(answers),
  ).length;

  const understoodKeys = Object.keys(answers).filter((k) => answers[k] !== null && answers[k] !== undefined && (Array.isArray(answers[k]) ? answers[k].length > 0 : true));
  const stillOpenKeys = Object.keys(UNDERSTOOD_LABELS).filter((k) => {
    if (answers[k] !== undefined && answers[k] !== null) return false;
    // Investor-only labels stay hidden until the user picks 'invertir'
    if ((k === 'expected_roi' || k === 'investment_horizon') && answers.objective !== 'invertir') return false;
    if (k === 'objective' && answers.operation !== 'venta') return false;
    return true;
  });

  const progressPct = Math.min(100, (understoodKeys.length / Math.max(1, applicableTurnsCount)) * 100);

  const persistAnswer = async (key, value) => {
    setSaving(true);
    try {
      // Para presupuesto, el Confidence Graph espera un dict {min?, max?}.
      // Los turnos ofrecen sólo max — envolvemos aquí.
      let payload = value;
      if (key === 'budget' && typeof value === 'number') {
        payload = { max: value };
      }
      await advisor.setConfidenceAttribute(key, payload);
      setAnswers((prev) => ({ ...prev, [key]: value }));
    } finally {
      setSaving(false);
    }
  };

  const handleSelect = async (value) => {
    if (!currentTurn) return;
    const dwellMs = Date.now() - turnStartMs.current;
    const turnKey = currentTurn.key;
    const turnIdx = step;
    if (value === currentTurn.skipValue) {
      // salto sin escribir — avanzamos con reacción de "está bien no saberlo"
      setReaction(reactionFor(currentTurn.key, currentTurn.skipValue));
      advisor.recordGuidedEvent('turn_skipped', {
        turnKey, turnIndex: turnIdx, dwellMs,
      });
      setStep((s) => s + 1);
      turnStartMs.current = Date.now();
      return;
    }
    await persistAnswer(currentTurn.key, value);
    setReaction(reactionFor(currentTurn.key, value));
    setFreeText('');
    setMultiSelection([]);
    // Telemetría: valor sólo si es string corto (privacidad — no free-text extenso)
    const safeValue = typeof value === 'string' && value.length <= 60 ? value : null;
    advisor.recordGuidedEvent('turn_answered', {
      turnKey, turnIndex: turnIdx, dwellMs,
      extra: safeValue ? { value: safeValue } : {},
    });
    setStep((s) => s + 1);
    turnStartMs.current = Date.now();
    // Cargar recomendaciones tempranas cuando llevemos ≥2 fundamentales
    if (understoodKeys.length + 1 >= 2 && !recommendations) {
      loadEarlyRecommendations();
    }
  };

  const handleFreeText = async () => {
    if (!freeText.trim() || !currentTurn) return;
    if (currentTurn.freeTextParser === 'number') {
      const digits = freeText.replace(/[^\d]/g, '');
      const parsed = parseInt(digits, 10);
      if (!parsed || parsed <= 0) return;
      await handleSelect(parsed);
      return;
    }
    await handleSelect(freeText.trim());
  };

  const handleMultiContinue = async () => {
    if (!currentTurn) return;
    // Registra la respuesta (lista, puede ir vacía si `allowsEmpty`).
    await handleSelect([...multiSelection]);
  };

  const toggleMultiOption = (val) => {
    setMultiSelection((prev) => {
      const set = new Set(prev);
      if (set.has(val)) set.delete(val); else set.add(val);
      return Array.from(set);
    });
  };

  const handleBack = () => {
    // Limpiar la micro-reacción al volver — refleja mejor que retrocedimos.
    setReaction(null);
    if (step > 0) setStep((s) => s - 1);
    else if (step === 0) setStep(-1);
  };

  const loadEarlyRecommendations = async () => {
    const res = await advisor.recommend({ limit: 3 });
    const items = (res?.items && Array.isArray(res.items)) ? res.items : [];
    setRecommendations(items);
    if (items.length > 0 && !recsShownRef.current) {
      recsShownRef.current = true;
      advisor.recordGuidedEvent('recommendations_shown', {
        extra: { count: items.length },
      });
    }
  };

  useEffect(() => {
    if (finished && !finishedFiredRef.current) {
      finishedFiredRef.current = true;
      advisor.recordGuidedEvent('finished', {
        sessionMs: Date.now() - sessionStartMs.current,
        extra: { answered_count: Object.keys(answers).length },
      });
    }
    // Al terminar el flujo (última pregunta respondida), re-ejecutamos siempre
    // el RecommendationEngine + ExplanationEngine para que la respuesta de zona
    // esté reflejada en el ranking final. Fuerza refresh sin importar si ya
    // había items desde turnos previos.
    if (finished) loadEarlyRecommendations();
  }, [finished]);

  // Cuando cambia el step activo, disparamos `turn_shown` y reseteamos el timer.
  useEffect(() => {
    if (step >= 0 && step < TURNS.length) {
      const t = TURNS[step];
      // Evitar disparar dos veces el mismo turno (por doble render en dev)
      if (currentTurnKeyRef.current !== t.key) {
        currentTurnKeyRef.current = t.key;
        turnStartMs.current = Date.now();
        advisor.recordGuidedEvent('turn_shown', {
          turnKey: t.key, turnIndex: step,
        });
      }
    }
  }, [step]);

  // Abandono: si el usuario cierra la pestaña o navega fuera antes de terminar,
  // fire-and-forget vía sendBeacon (`recordGuidedEvent` usa beacon internamente).
  useEffect(() => {
    const onBeforeUnload = () => {
      if (finishedFiredRef.current) return;
      if (step < 0) return; // aún en welcome, no es abandono real
      const dwellMs = Date.now() - turnStartMs.current;
      const sessionMs = Date.now() - sessionStartMs.current;
      advisor.recordGuidedEvent('abandoned', {
        turnKey: currentTurn?.key || null,
        turnIndex: step,
        dwellMs,
        sessionMs,
        extra: { answered_count: Object.keys(answers).length },
      });
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [step, currentTurn, answers]);

  /* ─── Renders ─────────────────────────────────────────────────── */

  if (!ctxReady) {
    return (
      <div className="min-h-screen bg-habita-mint/30 grid place-items-center">
        <div className="animate-pulse text-habita-dark/50 text-sm">Preparando tu Advisor…</div>
      </div>
    );
  }

  if (step === -1) {
    return <WelcomeScreen onStart={() => setStep(0)} onSkip={() => navigate('/buscar')} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-habita-mint/30 via-white to-white" data-testid="advisor-guided">
      <SEO
        title="Habita Advisor · Descubre lo que buscas"
        description="Habla con Habita Advisor y descubre en menos de 2 minutos qué propiedad encaja con tu vida. Recomendaciones honestas, sin presión."
      />
      {/* Progress sutil */}
      <div className="fixed top-0 left-0 right-0 h-1 bg-gray-100 z-10">
        <div
          className="h-full bg-habita transition-all duration-500"
          style={{ width: `${progressPct}%` }}
          data-testid="guided-progress-bar"
        />
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-14 pb-16">
        {/* Header liviano */}
        <div className="flex items-center gap-3 mb-10">
          <div className="w-10 h-10 rounded-full bg-habita text-white grid place-items-center">
            <Compass className="w-5 h-5" />
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-widest text-habita-dark/70 font-semibold">
              Habita Advisor
            </p>
            <p className="text-sm text-gray-600">
              Vamos entendiendo qué buscas — te acompaño paso a paso.
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-[1fr_320px] gap-8">
          {/* Columna principal: turno actual o cierre */}
          <div ref={cardRef}>
            {!finished ? (
              <TurnCard
                turn={currentTurn}
                answers={answers}
                onSelect={handleSelect}
                freeText={freeText}
                setFreeText={setFreeText}
                onFreeText={handleFreeText}
                saving={saving}
                canBack={step > 0}
                onBack={handleBack}
                reaction={reaction}
                multiSelection={multiSelection}
                onToggleMulti={toggleMultiOption}
                onMultiContinue={handleMultiContinue}
              />
            ) : (
              <FinishedCard
                recommendations={recommendations}
                answers={answers}
                onCalibrationChange={loadEarlyRecommendations}
                onSeeAll={() => {
                  advisor.recordGuidedEvent('navigated_to_workspace', {
                    sessionMs: Date.now() - sessionStartMs.current,
                  });
                  navigate('/mi-decision');
                }}
                onKeepBrowsing={() => navigate('/buscar')}
              />
            )}

            {/* Footer de continuidad — refuerza que se guarda automáticamente */}
            {!finished && (
              <p
                className="mt-4 text-center text-xs text-gray-500"
                data-testid="guided-continuity-note"
              >
                Se guarda automáticamente — puedes cerrar y volver cuando quieras desde <span className="text-habita-dark font-medium">Mi decisión</span>.
              </p>
            )}
          </div>

          {/* Sidebar: lo que ya entendí + próximas piezas + recomendaciones tempranas */}
          <aside className="space-y-6" data-testid="guided-sidebar">
            <UnderstoodBlock
              understood={understoodKeys.map((k) => ({ key: k, value: answers[k] }))}
              stillOpen={stillOpenKeys}
            />

            {recommendations && recommendations.length > 0 && (
              <EarlyRecommendations items={recommendations} />
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}


/* ─── Sub-components ─────────────────────────────────────────────── */

function WelcomeScreen({ onStart, onSkip }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-habita-mint/40 to-white grid place-items-center px-4">
      <SEO
        title="Habita Advisor · Descubre lo que buscas"
        description="Habla con Habita Advisor y descubre en menos de 2 minutos qué propiedad encaja con tu vida. Recomendaciones honestas, sin presión."
      />
      <div className="max-w-2xl text-center" data-testid="guided-welcome">
        <div className="w-16 h-16 rounded-full bg-habita text-white grid place-items-center mx-auto mb-6">
          <Compass className="w-7 h-7" />
        </div>
        <p className="text-[11px] uppercase tracking-widest text-habita font-semibold mb-1">
          Habita Advisor
        </p>
        <p className="text-sm text-habita-dark/70 mb-5">
          Tu asesor para tomar mejores decisiones inmobiliarias.
        </p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-habita-dark leading-tight mb-6">
          Hola. Vamos a encontrar la mejor decisión para ti.
        </h1>
        <p className="text-lg text-gray-700 mb-10 max-w-xl mx-auto">
          Cuéntame qué buscas en unos minutos y empezaré a mostrarte propiedades que se
          ajusten a lo que necesitas — y a explicarte por qué encaja cada una.
        </p>
        <div className="flex items-center justify-center gap-3 flex-wrap">
          <button
            onClick={onStart}
            data-testid="guided-start-primary"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-habita text-white font-semibold hover:opacity-90 transition-opacity"
          >
            Empecemos <ArrowRight className="w-4 h-4" />
          </button>
          <button
            onClick={onSkip}
            data-testid="guided-start-skip"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-full border border-gray-300 text-gray-700 hover:bg-white transition-colors"
          >
            Prefiero explorar solo
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-6">
          Toma menos de 2 minutos — y cada respuesta ya se traduce en propiedades mejor pensadas.
        </p>
      </div>
    </div>
  );
}


function TurnCard({
  turn, onSelect, freeText, setFreeText, onFreeText, saving, canBack, onBack,
  answers, reaction, multiSelection, onToggleMulti, onMultiContinue,
}) {
  if (!turn) return null;

  // Opciones dinámicas por city para neighborhood
  let options = turn.options;
  if (turn.key === 'neighborhood' && turn.dynamicOptionsByCity) {
    const city = answers.city;
    if (city && turn.dynamicOptionsByCity[city]) {
      options = turn.dynamicOptionsByCity[city].map((v) => ({ value: v, label: v }));
    }
  }
  // Opciones dinámicas por operación para budget (arriendo tiene rangos más bajos)
  if (turn.key === 'budget' && turn.dynamicOptionsByOperation) {
    const op = answers.operation;
    if (op && turn.dynamicOptionsByOperation[op]) {
      options = turn.dynamicOptionsByOperation[op];
    }
  }

  return (
    <div
      className="bg-white rounded-3xl p-6 sm:p-10 border border-gray-100 shadow-sm"
      data-testid={`guided-turn-${turn.id}`}
    >
      {/* Micro-reacción del turno anterior — hace que la respuesta se sienta ESCUCHADA */}
      {reaction && (
        <div
          className="mb-6 inline-flex items-start gap-2 px-4 py-2.5 rounded-2xl bg-habita-mint/60 border border-habita/20 max-w-[92%]"
          data-testid="guided-micro-reaction"
        >
          <Sparkles className="w-4 h-4 text-habita mt-0.5 shrink-0" />
          <p className="text-sm text-habita-dark italic">{reaction}</p>
        </div>
      )}

      <p className="text-sm text-gray-500 mb-2 italic">{turn.line}</p>
      <h2
        className="font-serif text-3xl sm:text-4xl font-bold text-habita-dark leading-snug mb-3"
        data-testid="guided-prompt"
      >
        {turn.prompt}
      </h2>
      {turn.hint && (
        <p className="text-sm text-gray-600 mb-8">{turn.hint}</p>
      )}

      {/* Opciones */}
      {turn.layout === 'big-cards' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {options.map((o) => {
            const Icon = o.icon;
            return (
              <button
                key={o.value}
                onClick={() => onSelect(o.value)}
                disabled={saving}
                data-testid={`guided-option-${o.value}`}
                className="group flex items-center gap-4 rounded-2xl border-2 border-gray-200 hover:border-habita hover:bg-habita-mint/40 p-5 transition-all disabled:opacity-60 text-left"
              >
                {Icon && <div className="w-11 h-11 rounded-full bg-habita-mint text-habita grid place-items-center shrink-0 group-hover:bg-habita group-hover:text-white transition-colors"><Icon className="w-5 h-5" /></div>}
                <span className="font-serif text-xl font-bold text-habita-dark">{o.label}</span>
              </button>
            );
          })}
        </div>
      )}

      {turn.layout === 'icon-grid' && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {options.map((o) => {
            const Icon = o.icon;
            return (
              <button
                key={o.value}
                onClick={() => onSelect(o.value)}
                disabled={saving}
                data-testid={`guided-option-${o.value}`}
                className="flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-gray-200 hover:border-habita hover:bg-habita-mint/40 p-5 transition-all disabled:opacity-60"
              >
                {Icon && <Icon className="w-6 h-6 text-habita" />}
                <span className="font-medium text-habita-dark text-sm">{o.label}</span>
              </button>
            );
          })}
        </div>
      )}

      {turn.layout === 'chips' && (
        <div className="flex flex-wrap gap-2">
          {options.map((o) => (
            <button
              key={o.value}
              onClick={() => onSelect(o.value)}
              disabled={saving}
              data-testid={`guided-option-${o.value}`}
              className="inline-flex items-center gap-1.5 h-11 px-5 rounded-full bg-white border-2 border-gray-200 hover:border-habita hover:bg-habita-mint/40 text-habita-dark font-medium transition-all disabled:opacity-60"
            >
              {o.label}
            </button>
          ))}
        </div>
      )}

      {turn.layout === 'text-input' && (
        <KeyLocationInput
          placeholder={turn.placeholder}
          onSubmit={(resolved) => onSelect(resolved)}
          onSkip={turn.allowsEmpty ? () => onSelect(null) : undefined}
          disabled={saving}
        />
      )}

      {turn.layout === 'priority-ranker' && (
        <PriorityRanker
          onSubmit={(order) => onSelect(order)}
          disabled={saving}
        />
      )}

      {turn.layout === 'multi-toggle' && (
        <div data-testid="guided-multi-container">
          <div className="flex flex-wrap gap-2 mb-6">
            {options.map((o) => {
              const active = multiSelection.includes(o.value);
              return (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => onToggleMulti(o.value)}
                  disabled={saving}
                  data-testid={`guided-option-${o.value}`}
                  aria-pressed={active}
                  className={
                    active
                      ? 'inline-flex items-center gap-2 h-11 px-5 rounded-full bg-habita text-white border-2 border-habita font-medium transition-all disabled:opacity-60'
                      : 'inline-flex items-center gap-2 h-11 px-5 rounded-full bg-white border-2 border-gray-200 hover:border-habita hover:bg-habita-mint/40 text-habita-dark font-medium transition-all disabled:opacity-60'
                  }
                >
                  {active && <Check className="w-4 h-4" />}
                  {o.label}
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onMultiContinue}
              disabled={saving || (!turn.allowsEmpty && multiSelection.length === 0)}
              data-testid="guided-multi-continue"
              className="inline-flex items-center gap-2 h-11 px-6 rounded-full bg-habita text-white font-semibold hover:opacity-90 transition-opacity disabled:opacity-40"
            >
              Continuar <ArrowRight className="w-4 h-4" />
            </button>
            {turn.allowsEmpty && multiSelection.length === 0 && (
              <span className="text-xs text-gray-500">Puedes continuar sin marcar nada si no aplica.</span>
            )}
            {multiSelection.length > 0 && (
              <span
                className="text-xs text-habita-dark"
                data-testid="guided-multi-count"
              >
                {multiSelection.length} seleccionado{multiSelection.length === 1 ? '' : 's'}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Free text */}
      {turn.allowsFreeText && (
        <div className="mt-5 flex items-center gap-2">
          <input
            type="text"
            value={freeText}
            onChange={(e) => setFreeText(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && onFreeText()}
            placeholder={turn.freeTextPlaceholder || 'Escríbelo con tus palabras…'}
            data-testid="guided-freetext"
            className="flex-1 h-11 px-4 rounded-full border-2 border-gray-200 focus:border-habita focus:outline-none text-sm"
          />
          <button
            onClick={onFreeText}
            disabled={!freeText.trim() || saving}
            data-testid="guided-freetext-submit"
            className="h-11 px-4 rounded-full bg-habita text-white text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-40"
          >
            Seguir
          </button>
        </div>
      )}

      {/* Skip */}
      {turn.allowsSkip && (
        <button
          onClick={() => onSelect(turn.skipValue)}
          disabled={saving}
          data-testid="guided-skip"
          className="mt-4 text-sm text-habita hover:text-habita-dark hover:underline"
        >
          {turn.skipLabel}
        </button>
      )}

      {/* Nav secundaria */}
      {canBack && (
        <button
          onClick={onBack}
          className="mt-8 inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-habita-dark"
          data-testid="guided-back"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Volver al paso anterior
        </button>
      )}
    </div>
  );
}


function UnderstoodBlock({ understood, stillOpen }) {
  return (
    <div
      className="bg-white rounded-2xl border border-gray-100 p-5"
      data-testid="guided-understood-block"
    >
      <p className="text-[11px] uppercase tracking-widest text-habita-dark/70 font-semibold mb-3">
        Lo que ya entendí
      </p>
      {understood.length === 0 ? (
        <p className="text-sm text-gray-500 italic mb-4">Todavía estamos empezando.</p>
      ) : (
        <ul className="space-y-2 mb-4" data-testid="understood-list">
          {understood.map((u) => (
            <li key={u.key} className="flex items-start gap-2 text-sm">
              <Check className="w-4 h-4 text-habita mt-0.5 shrink-0" />
              <span>
                <span className="font-medium capitalize text-habita-dark">{UNDERSTOOD_LABELS[u.key] || u.key}</span>
                <span className="text-gray-500">: {formatUnderstoodValue(u.key, u.value)}</span>
              </span>
            </li>
          ))}
        </ul>
      )}

      {stillOpen.length > 0 && (
        <>
          <p className="text-[11px] uppercase tracking-widest text-gray-400 font-semibold mb-2 mt-3">
            Aún estoy conociendo
          </p>
          <div className="flex flex-wrap gap-1.5" data-testid="still-open-list">
            {stillOpen.slice(0, 5).map((k) => (
              <span key={k} className="inline-flex items-center px-2.5 h-6 rounded-full bg-gray-100 text-gray-500 text-xs capitalize">
                {UNDERSTOOD_LABELS[k] || k}
              </span>
            ))}
          </div>
        </>
      )}
    </div>
  );
}


function EarlyRecommendations({ items }) {
  return (
    <div
      className="bg-habita-mint/40 border border-habita/20 rounded-2xl p-5"
      data-testid="guided-early-recommendations"
    >
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-4 h-4 text-habita" />
        <p className="text-[11px] uppercase tracking-widest text-habita-dark font-semibold">
          Ya puedo mostrarte algo
        </p>
      </div>
      <p className="text-sm text-gray-700 mb-3">
        Con lo que sé hasta ahora, estas propiedades podrían encajar:
      </p>
      <ul className="space-y-2.5">
        {items.slice(0, 3).map((r, i) => {
          const snap = r.snapshot || {};
          const inv = snap.investment;
          const habita = snap.habita_score;
          const title = snap.title || `Propiedad recomendada #${i + 1}`;
          const summary = r.explanation?.human_readable || '';
          const href = `/propiedad/${r.item_id || snap.id || ''}`;
          const cover = snap.cover_image || null;
          const city = snap.city;
          return (
            <li key={r.item_id || i} className="text-sm">
              <a
                href={href}
                className="flex gap-2.5 bg-white rounded-lg p-2 border border-transparent hover:border-habita/40 transition-colors"
                data-testid={`guided-early-item-${i}`}
              >
                {cover ? (
                  <img
                    src={cover}
                    alt={title}
                    loading="lazy"
                    className="w-16 h-16 rounded-md object-cover shrink-0 bg-gray-100"
                    data-testid={`guided-early-item-image-${i}`}
                    onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
                  />
                ) : (
                  <div className="w-16 h-16 rounded-md bg-habita-mint grid place-items-center shrink-0">
                    <Sparkles className="w-5 h-5 text-habita" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="block font-medium text-habita-dark text-sm line-clamp-1 flex-1 min-w-0">
                      {title}
                    </span>
                    {habita && (
                      <HabitaScorePill score={habita} testId={`guided-early-habita-${i}`} />
                    )}
                    {inv?.is_opportunity && (
                      <span
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-500 text-white text-[9px] font-bold uppercase tracking-widest shrink-0"
                        data-testid={`guided-early-opportunity-${i}`}
                      >
                        <TrendingUp className="w-2.5 h-2.5" /> Oportunidad
                      </span>
                    )}
                  </div>
                  {city && (
                    <span className="block text-[11px] uppercase tracking-wide text-gray-400 mt-0.5">
                      {city}
                    </span>
                  )}
                  {inv && (
                    <span className="block text-[11px] text-habita font-semibold mt-0.5" data-testid={`guided-early-metric-${i}`}>
                      Renta {inv.estimated_rent_yield_pct}% · Valorización {inv.estimated_appreciation_pct}%
                    </span>
                  )}
                  {summary && (
                    <span className="block text-xs text-gray-500 mt-0.5 line-clamp-2">
                      {summary}
                    </span>
                  )}
                </div>
              </a>
            </li>
          );
        })}
      </ul>
    </div>
  );
}


function FinishedCard({ recommendations, onSeeAll, onKeepBrowsing, answers, onCalibrationChange }) {
  const top = (recommendations && recommendations[0]) || null;
  const rest = (recommendations || []).slice(1, 3);
  const hasResults = top !== null;

  // ── Captura del comprador (Punto A general + Punto B contextual) ──
  // `buyerLeadExists` se fija al montar: si ya hay lead, no mostramos el form
  // general. Tras capturar NO desmontamos el form (muestra su propio éxito);
  // la persistencia en sessionStorage evita re-mostrarlo en próximas visitas.
  const [buyerLeadExists] = useState(!!getBuyerLead());
  const [dismissed, setDismissed] = useState(isBuyerCaptureDismissed());
  const [pendingInterest, setPendingInterest] = useState(null);
  const showCapture = !buyerLeadExists && (!dismissed || !!pendingInterest);

  const handleFirmPickSaved = (interest) => {
    const existing = getBuyerLead();
    if (existing?.email) {
      // Ya hay lead comprador → enriquecer interests[] SIN mostrar formulario.
      api.advisorCapture({
        name: existing.name, email: existing.email, phone: existing.phone,
        intent: intentFromOperation(answers.operation),
        context: buildAdvisorContext(answers),
        advisor_context_id: advisor.getActiveContextId?.() || null,
        interest, source: 'advisor_firm_pick', session_id: getSessionId(),
      }).catch(() => { /* silencioso */ });
    } else {
      // Intención fuerte → re-surge la captura (aunque la hubiera descartado).
      setPendingInterest(interest);
    }
  };

  // Empty state: sin candidatos que crucen el pipeline. Ofrecemos alternativas
  // amables (relajar zona, precio, tipo) sin generar frustración.
  if (!hasResults) {
    const city = answers?.city;
    const type = answers?.property_type;
    const searchFallback = `/buscar${city ? `?ciudad=${encodeURIComponent(city)}` : ''}`;
    return (
      <div className="bg-white rounded-3xl p-6 sm:p-10 border border-gray-100 shadow-sm" data-testid="guided-finished-empty">
        <p className="text-sm text-gray-500 mb-2 italic">Con lo que tengo, todavía no encuentro algo que cuadre.</p>
        <h2 className="font-serif text-3xl sm:text-4xl font-bold text-habita-dark leading-snug mb-3">
          No es un no — es un &ldquo;probemos con otra cosa&rdquo;.
        </h2>
        <p className="text-gray-600 mb-6 max-w-xl">
          No encontré propiedades que cumplan todos tus criterios en este momento. Esto suele pasar cuando la zona o los indispensables son muy específicos. Podemos probar:
        </p>
        <ul className="space-y-2.5 mb-8" data-testid="guided-empty-suggestions">
          <li className="flex items-start gap-2 text-sm text-habita-dark">
            <Sparkles className="w-4 h-4 text-habita mt-0.5 shrink-0" />
            <span>Ampliar la <span className="font-semibold">zona</span> — a veces el barrio ideal está a 10 minutos del que pensabas.</span>
          </li>
          <li className="flex items-start gap-2 text-sm text-habita-dark">
            <Sparkles className="w-4 h-4 text-habita mt-0.5 shrink-0" />
            <span>Subir un poco el <span className="font-semibold">presupuesto máximo</span> — con frecuencia hay opciones muy cerca del rango.</span>
          </li>
          <li className="flex items-start gap-2 text-sm text-habita-dark">
            <Sparkles className="w-4 h-4 text-habita mt-0.5 shrink-0" />
            <span>Quitar algún <span className="font-semibold">indispensable</span> (parqueadero, ascensor, balcón) y volver a mirar.</span>
          </li>
        </ul>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={onSeeAll}
            data-testid="guided-empty-edit-context"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-habita text-white font-semibold hover:opacity-90 transition-opacity"
          >
            <Compass className="w-4 h-4" /> Ajustar mi contexto
          </button>
          <a
            href={searchFallback}
            data-testid="guided-empty-browse-fallback"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-full border border-gray-300 text-gray-700 hover:bg-habita-mint/40 transition-colors"
          >
            Explorar propiedades{city ? ` en ${city}` : ''}
          </a>
        </div>

        {/* Captura del comprador — útil sobre todo aquí: avisar cuando aparezca algo */}
        {showCapture && (
          <AdvisorCaptureForm
            mode="general"
            answers={answers}
            onCaptured={(info) => { setBuyerLead(info); }}
            onDismiss={() => { dismissBuyerCapture(); setDismissed(true); }}
          />
        )}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-3xl p-6 sm:p-10 border border-gray-100 shadow-sm" data-testid="guided-finished">
      <p className="text-sm text-gray-500 mb-2 italic">Tenemos suficiente para empezar.</p>
      <h2 className="font-serif text-3xl sm:text-4xl font-bold text-habita-dark leading-snug mb-3">
        Con esto ya puedo acompañarte mejor.
      </h2>
      <p className="text-gray-600 mb-6 max-w-xl">
        A medida que veas propiedades, iré ajustando las recomendaciones — y podemos seguir refinando
        cuando quieras desde tu espacio Mi decisión.
      </p>

      {/* Ficha del top pick del RecommendationEngine + explicación del ExplainabilityEngine */}
      {top && <FinishedRecommendationCard item={top} context={{ answers }} onFirmPickSaved={handleFirmPickSaved} />}

      {/* Calibración 1-tap · Enhancement Fase 5 */}
      {top && (
        <div className="mt-6">
          <CalibrationQuestion onAnswered={() => {
            // Re-fetch recommendations con las priorities actualizadas.
            onCalibrationChange?.();
          }} />
        </div>
      )}

      {/* Captura del comprador — no bloquea las recomendaciones */}
      {showCapture && (
        <AdvisorCaptureForm
          mode={pendingInterest ? 'contextual' : 'general'}
          answers={answers}
          interest={pendingInterest}
          onCaptured={(info) => { setBuyerLead(info); setPendingInterest(null); }}
          onDismiss={() => { dismissBuyerCapture(); setDismissed(true); setPendingInterest(null); }}
        />
      )}

      {/* Alternativas complementarias (si hay) */}
      {rest.length > 0 && (
        <div className="mt-6" data-testid="finished-alternatives">
          <p className="text-[11px] uppercase tracking-widest text-habita font-semibold mb-3">
            También podrían encajar
          </p>
          <ul className="grid sm:grid-cols-2 gap-3">
            {rest.map((r, i) => {
              const snap = r.snapshot || {};
              const href = `/propiedad/${r.item_id || snap.id || ''}`;
              return (
                <li key={r.item_id || i}>
                  <a
                    href={href}
                    className="flex gap-2.5 p-2.5 rounded-xl border border-gray-200 hover:border-habita/40 transition-colors"
                    data-testid={`finished-alt-${i}`}
                  >
                    {snap.cover_image && (
                      <img
                        src={snap.cover_image}
                        alt={snap.title || ''}
                        loading="lazy"
                        className="w-14 h-14 rounded-md object-cover shrink-0 bg-gray-100"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-habita-dark line-clamp-1">
                        {snap.title || 'Propiedad'}
                      </p>
                      {snap.city && (
                        <p className="text-[11px] text-gray-500 mt-0.5">{snap.city}</p>
                      )}
                      {r.explanation?.human_readable && (
                        <p className="text-[11px] text-gray-600 mt-1 line-clamp-2 italic">
                          {r.explanation.human_readable}
                        </p>
                      )}
                    </div>
                  </a>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className="mt-8 flex flex-wrap gap-3">
        <button
          onClick={onSeeAll}
          data-testid="guided-goto-workspace"
          className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-habita text-white font-semibold hover:opacity-90 transition-opacity"
        >
          <Compass className="w-4 h-4" /> Ir a Mi decisión
        </button>
        <button
          onClick={onKeepBrowsing}
          data-testid="guided-keep-browsing"
          className="inline-flex items-center gap-2 h-12 px-6 rounded-full border border-gray-300 text-gray-700 hover:bg-habita-mint/40 transition-colors"
        >
          Ver más propiedades
        </button>
      </div>
    </div>
  );
}


function FinishedRecommendationCard({ item, context, onFirmPickSaved }) {
  const snap = item.snapshot || {};
  const inv = snap.investment;
  const habita = snap.habita_score;
  const deep = snap.deep_reasons;
  const explanation = item.explanation?.human_readable || '';
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const isConstructorProject = snap.source === 'constructor_project';
  // Para vivienda nueva no tenemos ficha aún (roadmap), así que el link primario
  // es el brochure del proyecto. Para propiedades usadas, mantenemos /propiedad/:id.
  const href = isConstructorProject
    ? (snap.brochure_url || '#')
    : `/propiedad/${item.item_id || snap.id || ''}`;
  const priceStr = typeof snap.price === 'number'
    ? `$${Math.round(snap.price / 1_000_000).toLocaleString('es-CO')}M`
    : null;

  // Interés concreto (para enriquecer interests[] del lead comprador).
  const buildInterest = () => ({
    type: isConstructorProject ? 'project' : 'property',
    id: isConstructorProject ? (snap.constructor_project_id || snap.id) : snap.id,
    title: snap.title || snap.constructor_project_name || null,
    city: snap.city || null,
    habita_score: habita?.total || null,
    typology_name: snap.typology_name || null,
  });

  // Si ya existe un lead comprador identificado, enriquecemos interests[] sin
  // formulario (WhatsApp / contacto). No crea otro lead ni fuerza captura.
  const enrichIfBuyerLead = () => {
    const existing = getBuyerLead();
    if (!existing?.email) return;
    api.advisorCapture({
      name: existing.name, email: existing.email, phone: existing.phone,
      intent: intentFromOperation(context?.answers?.operation),
      context: buildAdvisorContext(context?.answers || {}),
      advisor_context_id: advisor.getActiveContextId?.() || null,
      interest: buildInterest(), source: 'advisor_firm_pick', session_id: getSessionId(),
    }).catch(() => { /* silencioso */ });
  };

  // Lead inteligente para WhatsApp — incluye razonamiento del Advisor
  const buildLeadContext = () => {
    if (!habita) return null;
    const ctxLine = context?.answers?.budget_max
      ? `mi presupuesto de $${Math.round(context.answers.budget_max / 1_000_000).toLocaleString('es-CO')}M`
      : (context?.answers?.city ? `mi búsqueda en ${context.answers.city}` : null);
    return {
      habita_score: habita.total,
      budget_hint: ctxLine,
    };
  };
  const leadCtx = buildLeadContext();
  const waHref = snap.id
    ? buildPropertyWhatsAppLink({ property: { id: snap.id, title: snap.title, city: snap.city }, advisorContext: leadCtx })
    : null;

  return (
    <a
      href={href}
      target={isConstructorProject ? '_blank' : undefined}
      rel={isConstructorProject ? 'noopener noreferrer' : undefined}
      className={`block rounded-2xl overflow-hidden border-2 ${isConstructorProject ? 'border-indigo-400/50' : 'border-habita/40'} bg-white shadow-sm hover:shadow-md transition-shadow`}
      data-testid="finished-top-recommendation"
    >
      <div className="grid sm:grid-cols-[240px_1fr]">
        <div className="aspect-[4/3] sm:aspect-auto bg-gray-100 overflow-hidden relative">
          {snap.cover_image ? (
            <img
              src={snap.cover_image}
              alt={snap.title || 'Recomendación'}
              className="w-full h-full object-cover"
              loading="lazy"
              data-testid="finished-top-image"
            />
          ) : (
            <div className="w-full h-full grid place-items-center bg-habita-mint">
              <Sparkles className="w-8 h-8 text-habita" />
            </div>
          )}
          {inv?.is_opportunity && (
            <span
              className="absolute top-3 left-3 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-500 text-white text-[10px] font-bold uppercase tracking-widest shadow-md"
              data-testid="finished-top-opportunity-badge"
            >
              <TrendingUp className="w-3 h-3" /> Oportunidad de Inversión
            </span>
          )}
          {habita && (
            <div className="absolute top-3 right-3" data-testid="finished-top-habita-score">
              <HabitaScoreBadge score={habita} size="md" showLabel={false} />
            </div>
          )}
        </div>
        <div className="p-5">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            {isConstructorProject ? (
              <span
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-indigo-600 text-white text-[10px] uppercase tracking-widest font-bold"
                data-testid="new-project-badge"
              >
                <Building2 className="w-3 h-3" /> Proyecto Nuevo
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-habita-mint text-habita-dark text-[10px] uppercase tracking-widest font-bold">
                <Sparkles className="w-2.5 h-2.5" />
                Nuestra recomendación
              </span>
            )}
            {snap.stage && isConstructorProject && (
              <span className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold capitalize">
                Etapa: {snap.stage}
              </span>
            )}
          </div>
          {isConstructorProject ? (
            <>
              <h3 className="font-serif text-xl font-bold text-habita-dark leading-tight line-clamp-2" data-testid="finished-top-title">
                {snap.constructor_project_name || snap.title}
              </h3>
              {snap.typology_name && (
                <p
                  className="mt-1.5 text-sm text-indigo-700 font-semibold"
                  data-testid="new-project-typology-line"
                >
                  Tipología {snap.typology_name} recomendada para ti
                </p>
              )}
              {snap.city && (
                <p className="text-xs text-gray-500 mt-1">
                  {snap.neighborhood ? `${snap.neighborhood}, ` : ''}{snap.city}
                  {snap.developer_name && <span> · {snap.developer_name}</span>}
                </p>
              )}
            </>
          ) : (
            <>
              <h3 className="font-serif text-xl font-bold text-habita-dark leading-tight line-clamp-2" data-testid="finished-top-title">
                {snap.title || 'Propiedad'}
              </h3>
              {snap.city && (
                <p className="text-sm text-gray-500 mt-1">{snap.city}</p>
              )}
            </>
          )}

          {/* SD-002: mostrar la razón del ExplainabilityEngine tal cual (sin prefijo manual) */}
          <p
            className="mt-3 text-sm text-habita-dark bg-habita-mint/40 border-l-2 border-habita pl-3 py-2 rounded-r-md leading-relaxed"
            data-testid="finished-top-reason"
          >
            {explanation || 'Te recomendamos esta opción porque coincide con lo que nos contaste hasta ahora.'}
          </p>

          {habita && (
            <HabitaScoreHeadline score={habita} className="mt-2" />
          )}

          {/* Frase de valor: commute concreto (SD-020) */}
          {habita?.commute?.matched && habita.commute.travel_minutes <= 45 && (
            <p
              className="mt-2 text-xs text-habita-dark font-semibold flex items-center gap-1.5"
              data-testid="finished-top-commute"
            >
              <Compass className="w-3.5 h-3.5 text-habita" />
              A solo {habita.commute.travel_minutes} min de tu ubicación clave
              {habita.work_mode === 'presencial' && ' — clave para tu esquema presencial'}
            </p>
          )}

          {/* Métricas de inversión (sólo modo inversor) */}
          {inv && (
            <div className="mt-3 grid grid-cols-3 gap-2" data-testid="finished-top-investment">
              <div className="rounded-lg bg-habita-cream border border-habita/10 p-2">
                <p className="text-[9px] uppercase tracking-widest text-gray-500 font-semibold">Renta anual</p>
                <p className="font-serif text-lg font-bold text-habita mt-0.5">{inv.estimated_rent_yield_pct}%</p>
              </div>
              <div className="rounded-lg bg-habita-cream border border-habita/10 p-2">
                <p className="text-[9px] uppercase tracking-widest text-gray-500 font-semibold">Valorización</p>
                <p className="font-serif text-lg font-bold text-habita mt-0.5">{inv.estimated_appreciation_pct}%</p>
              </div>
              <div className="rounded-lg bg-habita-cream border border-habita/10 p-2">
                <p className="text-[9px] uppercase tracking-widest text-gray-500 font-semibold">Retorno total</p>
                <p className="font-serif text-lg font-bold text-habita mt-0.5">{inv.estimated_total_return_pct}%</p>
              </div>
              {inv.estimated_monthly_rent_cop && (
                <p className="col-span-3 text-[11px] text-gray-600 mt-0.5">
                  Renta mensual bruta estimada: <span className="font-semibold text-habita-dark">${inv.estimated_monthly_rent_cop.toLocaleString('es-CO')} COP</span>
                </p>
              )}
            </div>
          )}

          <div className="mt-3 flex items-center justify-between flex-wrap gap-2 text-xs text-gray-600">
            <div className="flex items-center gap-3">
              {typeof snap.beds === 'number' && <span>{snap.beds} hab</span>}
              {typeof snap.area === 'number' && <span>{snap.area} m²</span>}
              {snap.type && <span className="capitalize">{snap.type}</span>}
            </div>
            {priceStr && <p className="font-bold text-habita text-base">{priceStr}</p>}
          </div>

          {/* Habita Score Breakdown — 4 dimensiones visuales */}
          {habita && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <p className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold mb-3">
                Desglose del Habita Score
              </p>
              <HabitaScoreBreakdown score={habita} />
            </div>
          )}

          {/* Simulación financiera flash (solo si hay precio y es venta) */}
          {snap.price && snap.operation === 'venta' && (
            <div className="mt-4">
              <FlashFinancialSim price={snap.price} />
            </div>
          )}

          {/* 3 preguntas humanas · Capa de Decisión Profunda */}
          {deep && (
            <div className="mt-4">
              <p className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold mb-3">
                Lo que necesitas saber
              </p>
              <DeepReasonsCard reasons={deep} />
            </div>
          )}

          {/* Ver Brochure — CTA principal para vivienda nueva */}
          {isConstructorProject && snap.brochure_url && (
            <div className="mt-4">
              <a
                href={snap.brochure_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                data-testid="finished-top-brochure-btn"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-indigo-600 text-white font-semibold hover:bg-indigo-700 transition-colors"
              >
                Ver Brochure del proyecto
              </a>
              {snap.pricelist_url && (
                <a
                  href={snap.pricelist_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  data-testid="finished-top-pricelist-btn"
                  className="ml-2 inline-flex items-center gap-2 h-11 px-4 rounded-full border-2 border-indigo-500 text-indigo-700 font-semibold hover:bg-indigo-50 transition-colors"
                >
                  Lista de precios
                </a>
              )}
            </div>
          )}

          {/* WhatsApp lead inteligente */}
          {waHref && (
            <div className="mt-4">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  enrichIfBuyerLead();
                  window.open(waHref, '_blank', 'noopener,noreferrer');
                }}
                data-testid="finished-top-whatsapp"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-emerald-500 text-white font-semibold hover:bg-emerald-600 transition-colors"
              >
                <WhatsAppIcon className="w-4 h-4" />
                Contactar con contexto del Advisor
              </button>
              {habita && (
                <p className="text-[11px] text-gray-500 mt-2 leading-snug">
                  Enviaremos tu Habita Score de <span className="font-semibold text-habita">{habita.total}%</span> como respaldo — así el asesor sabe exactamente qué buscas.
                </p>
              )}
            </div>
          )}

          {/* Guardar como decisión firme (CONVERGE mode) */}
          {snap.id && !saved && (
            <button
              onClick={async (e) => {
                e.preventDefault();
                e.stopPropagation();
                if (saving) return;
                setSaving(true);
                try {
                  const ctxId = advisor.getActiveContextId();
                  if (!ctxId) throw new Error('sin contexto');
                  await api.saveFirmPick({
                    context_id: ctxId,
                    property_id: snap.id,
                    property_title: snap.title,
                    habita_score: habita?.total || null,
                    reason: deep?.why_best || explanation || null,
                    // Enriquecimiento para vivienda nueva — permite renderizar
                    // el CTA "Descargar Brochure" y el logo del developer en /mi-decision
                    ...(isConstructorProject ? {
                      source: 'constructor_project',
                      constructor_project_id: snap.constructor_project_id,
                      constructor_project_slug: snap.constructor_project_slug,
                      constructor_project_name: snap.constructor_project_name,
                      typology_name: snap.typology_name,
                      developer_name: snap.developer_name,
                      brochure_url: snap.brochure_url,
                      pricelist_url: snap.pricelist_url,
                      stage: snap.stage,
                      cover_image: snap.cover_image || (snap.images || [])[0] || null,
                    } : {}),
                  });
                  setSaved(true);
                  onFirmPickSaved?.(buildInterest());
                } catch {
                  /* silencioso */
                } finally {
                  setSaving(false);
                }
              }}
              disabled={saving}
              data-testid="finished-top-save-firm"
              className="mt-3 inline-flex items-center gap-2 h-11 px-5 rounded-full border-2 border-habita text-habita font-semibold hover:bg-habita hover:text-white transition-colors disabled:opacity-50"
            >
              {saving ? 'Guardando...' : 'Guardar como mi decisión firme'}
            </button>
          )}
          {saved && (
            <div
              className="mt-3 inline-flex items-center gap-2 text-sm text-habita bg-habita-mint/50 rounded-full px-4 py-2 font-semibold"
              data-testid="finished-top-saved-badge"
            >
              ✓ Guardada en Mi decisión
            </div>
          )}
        </div>
      </div>
    </a>
  );
}
